/**
 * ClubUsagePage.jsx — /meu-consumo/club (C3 visao TIME)
 *
 * Pagina detalhe do consumo TOTAL do clube.
 * Consome GET /api/me/usage/club via usageClubService.
 *
 * Permissao: GESTOR sempre, TREINADOR se 0 gestores. Outros: 403 -> tela honesta.
 *
 * UI_PADRAO: cinza-base, sem cor decorativa. Saturação de cota é informação,
 * não emergência — destaque por peso de fonte, nunca por vermelho/âmbar
 * (vermelho é reservado a alerta clínico).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, Zap, Bot, Building2, Info,
  ArrowLeft, RefreshCw, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSportsLang } from '../contexts/SportsLangContext';
import { getClubUsage, invalidateClubUsageCache } from '../services/usageClubService';

const I18N = {
  pt: {
    back: 'Voltar',
    title: 'Consumo do clube',
    subtitle: 'Detalhamento de todo o consumo do clube no mês corrente',
    refresh: 'Atualizar', refreshing: 'Atualizando...',
    loading: 'Carregando consumo do clube...',
    forbidden: 'Esta página é restrita a GESTOR — ou TREINADOR de clube sem gestor.',
    errorTitle: 'Não foi possível carregar',
    errorRetry: 'Tentar novamente',
    cardInteractions: 'Interações',
    cardInteractionsHelp: 'Total de chamadas de todos do clube',
    cardTokens: 'Tokens',
    cardTokensHelp: 'Soma de tokens processados',
    cardCost: 'Cota utilizada',
    cardCostHelp: 'Percentual da cota mensal de créditos do clube já usado',
    cardCostNoSub: 'Sem assinatura ativa — percentual não calculado',
    byAgentTitle: 'Por assistente',
    byAgentEmpty: 'Nenhum assistente usado neste mês.',
    colAgent: 'Assistente', colInteractions: 'Interações', colTokens: 'Tokens',
    contextTitle: 'Contexto do plano',
    contextLineClub: (club) => `Plano do clube: ${club}`,
    contextLinePlan: (plan, limit) => `${plan} — ${limit?.toLocaleString('pt-BR')} interações/mês para todo o clube`,
    contextHonest:
      'O limite é compartilhado por todos os usuários do clube. Não existe cota individual definida — cada chamada conta no total mostrado acima.',
    fallbackInfo: 'O número acima inclui consumo recuperado por fallback (rows com club_id NULL atribuídas via vínculo do usuário ao clube).',
    monthLabel: 'Período de referência',
    asOf: 'Atualizado em',
    viewerGestor: 'Você está vendo como GESTOR.',
    viewerCoach: 'Você está vendo porque o clube não tem GESTOR ativo — TREINADOR assume a visão.',
    saturationWarn: (pct) => `Atenção: ${pct}% do limite mensal usado.`,
  },
  en: {
    back: 'Back',
    title: 'Club usage',
    subtitle: 'Breakdown of all club usage this month',
    refresh: 'Refresh', refreshing: 'Refreshing...',
    loading: 'Loading club usage...',
    forbidden: 'This page is restricted to GESTOR — or TREINADOR of a club without a manager.',
    errorTitle: 'Could not load', errorRetry: 'Try again',
    cardInteractions: 'Interactions',
    cardInteractionsHelp: 'Total calls from everyone in the club',
    cardTokens: 'Tokens', cardTokensHelp: 'Sum of tokens processed',
    cardCost: 'Quota used', cardCostHelp: 'Percentage of the club\'s monthly credit quota already used',
    cardCostNoSub: 'No active subscription — percentage not computed',
    byAgentTitle: 'By assistant',
    byAgentEmpty: 'No assistant used this month.',
    colAgent: 'Assistant', colInteractions: 'Interactions', colTokens: 'Tokens',
    contextTitle: 'Plan context',
    contextLineClub: (club) => `Club plan: ${club}`,
    contextLinePlan: (plan, limit) => `${plan} — ${limit?.toLocaleString('en-US')} interactions/month for the whole club`,
    contextHonest:
      'The limit is shared by all club users. There is no individual quota defined — every call counts toward the total above.',
    fallbackInfo: 'The number above includes usage recovered by fallback (rows with NULL club_id attributed via user-to-club link).',
    monthLabel: 'Reference period', asOf: 'Updated at',
    viewerGestor: 'Viewing as MANAGER.',
    viewerCoach: 'Viewing because the club has no active MANAGER — COACH assumes the view.',
    saturationWarn: (pct) => `Warning: ${pct}% of monthly limit used.`,
  },
};

const fmtNumber = (n, lang) => n == null ? '—' : Number(n).toLocaleString(lang === 'en' ? 'en-US' : 'pt-BR');
// UI_PADRAO: consumo em % da cota (créditos = interações), sem cor de alerta.
const fmtPct = (used, limit, lang) => {
  if (!limit || Number(limit) <= 0) return '—';
  const p = (Number(used || 0) / Number(limit)) * 100;
  if (p > 0 && p < 1) return '<1%';
  return `${p.toLocaleString(lang === 'en' ? 'en-US' : 'pt-BR', { maximumFractionDigits: 1 })}%`;
};
const fmtAgent = (slug) => slug ? slug.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ') : '—';
const fmtTimestamp = (iso, lang) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(lang === 'en' ? 'en-US' : 'pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const ClubUsagePage = () => {
  const { user } = useAuth();
  const { lang } = useSportsLang();
  const t = I18N[lang] || I18N.pt;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = useCallback(async ({ force = false } = {}) => {
    if (force) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await getClubUsage({ force });
      if (!res) { setForbidden(true); setData(null); }
      else { setData(res); setForbidden(false); setFetchedAt(new Date().toISOString()); }
    } catch (err) {
      console.error('[ClubUsagePage] erro:', err);
      setError(err?.response?.data?.error || err?.message || 'unknown_error');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { if (user?.id) load(); }, [user, load]);
  const handleRefresh = () => { invalidateClubUsageCache(); load({ force: true }); };

  if (loading && !data && !forbidden) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-64 bg-gray-200 rounded" />
            <div className="h-4 w-96 bg-gray-200 rounded" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1,2,3].map(i => <div key={i} className="h-32 bg-white border border-gray-200 rounded-lg" />)}
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-6 text-center">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Link to="/feed" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium">
            <ArrowLeft size={16} /> {t.back}
          </Link>
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <ShieldCheck size={40} className="mx-auto mb-4 text-gray-400" />
            <p className="text-sm text-gray-600">{t.forbidden}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Link to="/feed" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium">
            <ArrowLeft size={16} /> {t.back}
          </Link>
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <AlertTriangle size={40} className="mx-auto mb-4" style={{ color: '#a93333' }} />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t.errorTitle}</h2>
            <p className="text-sm text-gray-600 mb-6">{String(error)}</p>
            <button onClick={() => load({ force: true })} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-gray-800">
              <RefreshCw size={14} /> {t.errorRetry}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { club, subscription, viewer, usage_current_month: usage } = data;
  const interactions = usage?.interactions || 0;
  const tokens = usage?.tokens || 0;
  const byAgent = Array.isArray(usage?.by_agent) ? usage.by_agent : [];
  const limit = subscription?.ai_credits_monthly;
  const pct = limit ? (interactions / limit) * 100 : null;
  const isSaturating = pct != null && pct >= 85;
  const period = usage?.period_start ? fmtTimestamp(usage.period_start, lang).split(',')[0] : '—';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/feed" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium">
          <ArrowLeft size={16} /> {t.back}
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <Building2 size={22} className="text-gray-700" />
              {t.title}
            </h1>
            <p className="text-sm text-gray-600">{t.subtitle}</p>
            <p className="text-xs text-gray-500 mt-2">
              <span className="font-medium text-gray-700">{club?.club_name}</span>
              <span className="mx-2 text-gray-300">·</span>
              {t.monthLabel}: <span className="font-medium">{period}</span>
              {fetchedAt && (<span className="ml-3 text-gray-400">· {t.asOf} {fmtTimestamp(fetchedAt, lang)}</span>)}
            </p>
            <p className="text-[11px] text-gray-400 mt-1 italic">
              {viewer?.can_see_reason === 'GESTOR' ? t.viewerGestor : t.viewerCoach}
            </p>
          </div>
          <button onClick={handleRefresh} disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50 transition-colors flex-shrink-0">
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? t.refreshing : t.refresh}
          </button>
        </div>

        {/* Aviso de saturação — neutro por design: destaque via peso de fonte,
            vermelho é reservado a alerta clínico (UI_PADRAO) */}
        {isSaturating && (
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Info size={18} className="flex-shrink-0 mt-0.5 text-gray-700" />
            <p className="text-sm font-semibold text-gray-900">{t.saturationWarn(Math.round(pct))}</p>
          </div>
        )}

        {/* Métricas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="md:col-span-3 bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Activity size={22} className="text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.cardInteractions}</p>
                <div className="flex items-baseline gap-3 mb-1">
                  <p className="text-3xl font-bold text-gray-900">{fmtNumber(interactions, lang)}</p>
                  {limit && (
                    <p className="text-base font-medium text-gray-500">de {fmtNumber(limit, lang)}</p>
                  )}
                </div>
                {limit && (
                  <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2 mb-1 overflow-hidden">
                    <div className="h-full rounded-full transition-all bg-gray-700"
                      style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">{t.cardInteractionsHelp}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Zap size={18} className="text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.cardTokens}</p>
                <p className="text-2xl font-bold text-gray-900">{fmtNumber(tokens, lang)}</p>
                <p className="text-xs text-gray-500 mt-2">{t.cardTokensHelp}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-5 md:col-span-2">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Activity size={18} className="text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.cardCost}</p>
                <p className="text-2xl font-bold text-gray-900">{fmtPct(interactions, limit, lang)}</p>
                <p className="text-xs text-gray-500 mt-2">{limit ? t.cardCostHelp : t.cardCostNoSub}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabela by_agent */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">{t.byAgentTitle}</h2>
          </div>
          {byAgent.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Bot size={32} className="mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-gray-500">{t.byAgentEmpty}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{t.colAgent}</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">{t.colInteractions}</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">{t.colTokens}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byAgent.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <Bot size={14} className="text-gray-400" />
                          <span className="font-medium text-gray-900">{fmtAgent(row.agent_name)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-right font-mono text-gray-900">{fmtNumber(row.interactions, lang)}</td>
                      <td className="px-6 py-3 text-right font-mono text-gray-700">{fmtNumber(row.tokens, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Fallback info */}
        {usage?.fallback_applied && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-xs text-gray-600 italic">
            {t.fallbackInfo} <span className="font-mono text-gray-500">(+{usage.rows_with_fallback})</span>
          </div>
        )}

        {/* Contexto plano */}
        <div className="bg-gray-100 border border-gray-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-md bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
              <Info size={16} className="text-gray-600" />
            </div>
            <div className="flex-1 min-w-0 text-sm text-gray-700">
              <p className="font-semibold text-gray-900 mb-2">{t.contextTitle}</p>
              {subscription ? (
                <>
                  <p className="mb-1">{t.contextLineClub(club?.club_name)}</p>
                  <p className="mb-3">{t.contextLinePlan(subscription.plan_key, subscription.ai_credits_monthly)}</p>
                  <p className="text-gray-600 leading-relaxed">{t.contextHonest}</p>
                </>
              ) : (
                <p className="text-gray-600 leading-relaxed">
                  {lang === 'en'
                    ? 'This club has no active subscription yet.'
                    : 'Este clube ainda não tem plano ativo.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubUsagePage;
